import React from 'react';
import SEO from '../components/SEO';
import { PersonalCategoryGrid } from '../components/CategoryGrid';
import ProductGrid from '../components/ProductGrid';
import CataloguesSection from '../components/CataloguesSection';
import { ApproachSection, StatsBar, Differentiators } from '../components/Sections';
import { FAQSection, Testimonials } from '../components/FaqTestimonials';
import ContactForm from '../components/ContactForm';
import { InfoTicker } from '../components/Marquees';
import { PERSONAL_CATEGORIES, PRODUCTS, FAQS } from '../mock/mock';
import { Sparkles, ArrowRight } from 'lucide-react';

const PageHero = () => (
  <section className="relative bg-white overflow-hidden">
    <div className="absolute inset-0 pointer-events-none">
      <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full pastel-lavender opacity-70 blur-3xl" />
      <div className="absolute bottom-0 -left-20 w-72 h-72 rounded-full pastel-peach opacity-70 blur-3xl" />
    </div>
    <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <div className="inline-flex items-center gap-2 pastel-lavender text-[#5A4A6F] px-4 py-1.5 rounded-full text-xs uppercase tracking-widest">
          <Sparkles className="w-3.5 h-3.5" /> Personalised Gifting
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl lg:text-7xl text-[#2C2416] leading-[1.02] mt-5">
          Gifts, made <span className="text-[#C58B76]">just for them.</span>
        </h1>
        <p className="text-[#5B4A3A] mt-6 max-w-xl text-lg leading-relaxed">Wedding return gifts, birthdays, baby showers, and festivals. Beautifully wrapped and truly personal.</p>
        <div className="flex flex-wrap gap-3 mt-8">
          <a href="#collections" className="btn-primary px-6 py-3 rounded-full text-sm uppercase tracking-widest inline-flex items-center gap-2">Explore Gifts <ArrowRight className="w-4 h-4" /></a>
          <a href="#enquire" className="btn-outline px-6 py-3 rounded-full text-sm uppercase tracking-widest">Talk to a Stylist</a>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <img
          src="https://images.unsplash.com/photo-1592903297149-37fb25202dfa"
          alt="Luxury wedding return gift box by WrapYourWish"
          width="300"
          height="400"
          className="rounded-2xl aspect-[3/4] object-cover"
        />
        <img
          src="https://images.pexels.com/photos/14755150/pexels-photo-14755150.jpeg"
          alt="Personalized anniversary gift basket with artisanal fragrance"
          width="300"
          height="400"
          className="rounded-2xl aspect-[3/4] object-cover mt-10"
        />
        <img
          src="https://images.unsplash.com/photo-1513201099705-a9746e1e201f"
          alt="Personalized baby shower gift hamper with pastel baby essentials"
          width="300"
          height="400"
          className="rounded-2xl aspect-[3/4] object-cover -mt-6"
        />
        <img
          src="https://images.unsplash.com/photo-1606498679463-30a0eb8824e1"
          alt="Bespoke birthday gift box with custom engraving"
          width="300"
          height="400"
          className="rounded-2xl aspect-[3/4] object-cover"
        />
      </div>
    </div>
  </section>
);

const PersonalisedGifting = () => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.a,
      },
    })),
  };

  return (
    <main>
      <SEO
        title="Personalised Gifting & Luxury Event Hampers | WrapYourWish"
        description="Curated wedding return gifts in bulk, baby shower hampers, birthday & anniversary gift boxes. Tailored with names, luxury packaging & PAN India delivery."
        keywords="wedding return gifts luxury bulk, personalized baby shower hampers, curated anniversary gift baskets, personalized event hampers India, custom wedding favors"
        canonicalUrl="https://wrapyourwish.com/personalised-gifting"
        schemas={[faqSchema]}
      />
      <PageHero />
      <InfoTicker />
      <div id="collections" />
      <PersonalCategoryGrid items={PERSONAL_CATEGORIES} />
      <CataloguesSection />
      <ProductGrid title="Curated for the moments that matter" subtitle="Personalised gifting collections" products={PRODUCTS.slice(0, 8)} />
      <ApproachSection />
      <StatsBar />
      <Differentiators />
      <Testimonials />
      <FAQSection />
      <ContactForm source="personalised-gifting" />
    </main>
  );
};

export default PersonalisedGifting;
